import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useLocation, Link } from "wouter";
import { toast } from "sonner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getToken, setToken, setRefreshToken } from "@/lib/auth";
import { homeForRoles } from "@/lib/api";
import { friendlyError } from "@/lib/errors";

/**
 * Connexion par code OTP (sans mot de passe).
 *
 * Contrat backend réel (auth-service, /api/auth/** routé au gateway et
 * whitelisté — accessible SANS JWT) :
 *   POST /api/auth/otp/request  { contact }         -> { message }      (200 toujours)
 *   POST /api/auth/otp/verify   { contact, code }   -> AuthResponse     (200) | 401
 * `contact` = email OU numéro de téléphone. La vérification réussie renvoie
 * un AuthResponse (accessToken + user), exactement comme /api/auth/login :
 * on stocke le token puis on redirige selon le rôle.
 *
 * DÉPENDANCE LIB MANQUANTE (non créée — lib/api.ts hors périmètre) :
 *   - `requestOtp(contact): Promise<{ message: string }>`  -> POST /api/auth/otp/request
 *   - `verifyOtp(contact, code): Promise<AuthLoginResponse>` -> POST /api/auth/otp/verify
 * En attendant, on appelle l'API ici via fetch en réutilisant getToken()
 * (lib/auth.ts) pour le Bearer éventuel, comme le fait apiFetch (privé).
 *
 * NB : le proto V03 supposait un flux "VERIFY_PHONE" authentifié avec
 * purpose/channel/devCode — ces champs n'existent PAS côté backend. On
 * s'aligne sur le contrat réel (contact + code -> JWT).
 */

const BASE = (
  /* v8 ignore next -- BASE_URL est toujours défini par Vite (défaut "/"), fallback défensif */
  import.meta.env.BASE_URL ?? "/"
).replace(/\/$/, "");

// Redirection rôle-aware : déléguée à homeForRoles() (lib/api), qui lit le
// TABLEAU roles[] renvoyé À PLAT par l'auth-service.

/** Réponse plate de /api/auth/otp/verify (= login) : roles[] à la racine. */
interface OtpVerifyResponse {
  accessToken: string;
  refreshToken?: string;
  roles?: string[];
}

async function postAuth<T>(path: string, body: unknown): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  const ct = res.headers.get("content-type") ?? "";
  const payload = ct.includes("application/json")
    ? await res.json().catch(() => null)
    : null;
  if (!res.ok) {
    const msg =
      (payload && typeof payload === "object" && (payload as { error?: string }).error) ||
      (payload && typeof payload === "object" && (payload as { message?: string }).message) ||
      res.statusText ||
      `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return payload as T;
}

const contactSchema = z.object({
  contact: z.string().min(1, "Email ou numéro de téléphone requis"),
});
type ContactValues = z.infer<typeof contactSchema>;

const codeSchema = z.object({
  code: z
    .string()
    .min(4, "Le code doit contenir au moins 4 chiffres")
    .max(8, "Le code doit contenir au maximum 8 chiffres"),
});
type CodeValues = z.infer<typeof codeSchema>;

export default function Otp() {
  const [, setLocation] = useLocation();

  const [contact, setContact] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const contactForm = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { contact: "" },
  });

  const codeForm = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: "" },
  });

  async function requestCode(value: string): Promise<boolean> {
    try {
      await postAuth<{ message?: string }>("/api/auth/otp/request", {
        contact: value,
      });
      toast.success("Si un compte existe, un code vous a été envoyé");
      return true;
    } catch (err) {
      toast.error(friendlyError(err));
      return false;
    }
  }

  async function onRequest(values: ContactValues) {
    setRequesting(true);
    const ok = await requestCode(values.contact.trim());
    if (ok) setContact(values.contact.trim());
    setRequesting(false);
  }

  async function onResend() {
    /* v8 ignore start -- garde défensive : le bouton « Renvoyer » n'existe qu'à l'étape vérif (contact non null) */
    if (!contact) return;
    /* v8 ignore stop */
    setRequesting(true);
    await requestCode(contact);
    setRequesting(false);
  }

  async function onVerify(values: CodeValues) {
    /* v8 ignore start -- garde défensive : le formulaire code n'est rendu qu'à l'étape vérif (contact non null) */
    if (!contact) return;
    /* v8 ignore stop */
    setVerifying(true);
    try {
      const res = await postAuth<OtpVerifyResponse>("/api/auth/otp/verify", {
        contact,
        code: values.code,
      });
      setToken(res.accessToken);
      if (res.refreshToken) setRefreshToken(res.refreshToken);
      toast.success("Connexion réussie");
      window.location.assign(homeForRoles(res.roles));
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="bg-white rounded-3xl p-8 border border-orange-50 shadow-sm">
        <h1 className="text-2xl font-display font-bold text-navy mb-2">
          Connexion par code
        </h1>
        <p className="text-sm text-navy/60 mb-6">
          {contact
            ? `Saisissez le code envoyé à ${contact}.`
            : "Recevez un code à usage unique par email ou SMS pour vous connecter."}
        </p>

        {!contact ? (
          <Form {...contactForm}>
            <form
              onSubmit={contactForm.handleSubmit(onRequest)}
              className="space-y-4"
            >
              <FormField
                control={contactForm.control}
                name="contact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email ou téléphone</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="username"
                        placeholder="vous@exemple.com ou 07 00 00 00 00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full bg-coral hover:bg-[#d16b50] text-white rounded-xl mt-4"
                disabled={requesting}
              >
                {requesting ? "Envoi en cours…" : "Recevoir un code"}
              </Button>
            </form>
          </Form>
        ) : (
          <div className="space-y-4">
            <Form {...codeForm}>
              <form
                onSubmit={codeForm.handleSubmit(onVerify)}
                className="space-y-4"
              >
                <FormField
                  control={codeForm.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code reçu</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={8}
                          className="text-center tracking-[0.5em] font-mono text-xl"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full bg-navy hover:bg-navy/90 text-white rounded-xl mt-4"
                  disabled={verifying}
                >
                  {verifying ? "Vérification…" : "Se connecter"}
                </Button>
              </form>
            </Form>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => {
                  setContact(null);
                  codeForm.reset();
                }}
                className="text-navy/60 hover:underline"
              >
                Changer de contact
              </button>
              <button
                type="button"
                onClick={onResend}
                disabled={requesting}
                className="text-coral font-bold hover:underline disabled:opacity-50 disabled:no-underline"
              >
                {requesting ? "Envoi…" : "Renvoyer le code"}
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 text-center text-sm text-navy/70">
          Préférez le mot de passe ?{" "}
          <Link
            href="/auth/login"
            className="text-coral font-bold hover:underline"
          >
            Connexion classique
          </Link>
        </div>
      </div>
    </div>
  );
}
